/**
 * Returns a paginated subset of items based on the current page and page size.
 * If the page is out of bounds, it will return the last page.
 *
 * @template T The type of items in the array.
 * @param {Array<T>} items - The array of items to paginate.
 * @param {number} page - The current page number (1-based).
 * @param {number} pageSize - The number of items per page.
 * @returns {Array<T>} The paginated items for the given page.
 */

export function getPaginatedItems<T>(
  items: Array<T>,
  page: number,
  pageSize: number
) {
  const totalItems = items.length

  const { startIndex, endIndex } = getPaginationRange(
    totalItems,
    pageSize,
    page
  )

  return items.slice(startIndex - 1, endIndex)
}

/**
 * Calculates pagination display range and related values.
 *
 * @param {number} totalItems - Total number of items.
 * @param {number} pageSize - Number of items per page.
 * @param {number} currentPage - Current (1-based) page number.
 * @returns {{ totalPages: number, clampedPage: number, startIndex: number, endIndex: number }}
 *   Object containing totalPages, clampedPage, startItem, and endItem.
 */
export function getPaginationRange(
  totalItems: number,
  pageSize: number,
  currentPage: number
) {
  // Calculate the total number of pages, ensuring at least 1 page exists
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  // Clamp the currentPage so it stays within the valid page range (1 to totalPages)
  // clampedPage will be set to:
  //   - 1 if currentPage < 1,
  //   - totalPages if currentPage > totalPages,
  //   - currentPage if it's within range.
  const clampedPage = Math.min(Math.max(currentPage, 1), totalPages)

  const startIndex = totalItems === 0 ? 0 : (clampedPage - 1) * pageSize + 1
  const endIndex = Math.min(clampedPage * pageSize, totalItems)

  return { totalPages, clampedPage, startIndex, endIndex }
}
